import { formatShare } from '../../utils/formatShare';
import type { StoreSync, MapViolation, StoreSyncHealth } from './useBrandHomeData';
import type { StoreIssue } from './components/stores/StoreIssuesPanel';
import type { Severity } from './severityTones';

export interface StoreSetupStep {
  id: 'brand' | 'authorization' | 'integration' | 'products';
  label: string;
  detail: string;
  done: boolean;
  stateLabel: string;
}

export interface StoreViewModel {
  storeId: string;
  storeName: string;
  platforms: string[];
  /** True when the store has a connected commerce platform. */
  integrationConnected: boolean;
  lastSyncAt: string | null;
  includedProductCount: number;
  excludedProductCount: number;
  countsArePartial: boolean;
  activeMapViolations: number;
  issues: StoreIssue[];
  hasIssues: boolean;
  /** Worst issue severity for this store, or null when it has none. */
  worstIssueSeverity: Extract<Severity, 'blocking' | 'warning'> | null;
  /**
   * The store listed the brand but has not connected a platform or chosen
   * products. That is unfinished setup, not a sync failure.
   */
  setupIncomplete: boolean;
  setupSteps: StoreSetupStep[];
  authorizationApproved: boolean;
  /**
   * One line describing a dealer authorization that is not verified, or null
   * when it is. Mirrors the setup step's copy so the two never disagree.
   */
  authorizationNote?: string | null;
  /** Server-computed sync health (src/services/brandDashboardMetrics.ts). */
  syncHealth?: StoreSyncHealth;
}

// A curated selection (a store carrying part of the catalog) is the normal
// case, not an issue — only flag exclusions that look like a real gap.
const EXCLUSION_ISSUE_RATE = 0.3;
const EXCLUSION_ISSUE_MIN_COUNT = 10;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The one "needs attention" predicate for a store. The Stores syncing tile,
 * the attention queue and the store list all count stores with this.
 */
export const storeNeedsAttention = (store: Pick<StoreViewModel, 'worstIssueSeverity'>): boolean =>
  store.worstIssueSeverity !== null;

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('') || '?';

/**
 * Sync health comes from the server so the "Stores syncing" KPI and the
 * per-store issues share one definition and one staleness window. Rows from
 * an older API without it only get the setup / never-synced distinction —
 * staleness is never guessed client-side.
 */
const resolveSyncHealth = (
  store: StoreSync,
  integrationConnected: boolean,
  productsSelected: boolean
): StoreSyncHealth => {
  if (store.syncHealth) return store.syncHealth;
  if (!integrationConnected || !productsSelected) return 'setup_incomplete';
  return store.lastSyncAt ? 'syncing' : 'never_synced';
};

export const buildStoreViewModels = (
  stores: StoreSync[],
  mapViolations: MapViolation[],
  nowMs: number = Date.now()
): StoreViewModel[] => {
  return stores.map((store) => {
    const storeId = store.storeId || store.id;
    const storeName = store.storeName || 'Store';
    const lastSyncAt = store.lastSyncAt ?? null;
    const excludedProductCount = store.excludedProductCount ?? 0;
    const includedProductCount = store.includedProductCount ?? 0;
    const platforms = store.platforms || [];
    const integrationConnected = store.integrationConnected ?? platforms.length > 0;
    const productsSelected = store.productsSelected ?? includedProductCount > 0;
    const brandSelected = store.brandSelected !== false;
    const authorizationStatus = store.authorizationStatus ?? 'none';
    const authorizationApproved = authorizationStatus === 'verified';
    const syncHealth = resolveSyncHealth(store, integrationConnected, productsSelected);
    const setupIncomplete = syncHealth === 'setup_incomplete';
    const setupSteps = buildSetupSteps({
      brandSelected,
      authorizationStatus,
      integrationConnected,
      platforms,
      productsSelected,
      includedProductCount,
      countsArePartial: store.countsArePartial === true,
    });
    const storeViolations = mapViolations.filter(
      (v) => v.retailerId === storeId && v.status === 'active'
    );

    const issues: StoreIssue[] = [];

    // A store still setting up is not a sync failure, so only finished-setup
    // stores get sync issues. A stale store needs attention and is never
    // counted as syncing (server: storesSyncing).
    if (syncHealth === 'never_synced') {
      issues.push({
        severity: 'warning',
        title: 'Has not synced yet',
        meta: 'No sync has been recorded for this store.',
      });
    } else if (syncHealth === 'stale' && lastSyncAt) {
      const ageMs = nowMs - new Date(lastSyncAt).getTime();
      const days = Number.isFinite(ageMs) ? Math.max(0, Math.floor(ageMs / DAY_MS)) : 0;
      issues.push({
        severity: 'blocking',
        title: `No sync in ${days} day${days === 1 ? '' : 's'}`,
        meta: `Catalog updates are not reaching this store. Last sync ${new Date(lastSyncAt).toLocaleDateString()}.`,
        age: `${days}d`,
      });
    }

    // A store curating a subset of the catalog is normal, not an issue — only
    // flag exclusions large enough to look like a real gap, not a deliberate
    // assortment choice.
    const totalForStore = includedProductCount + excludedProductCount;
    const exclusionRate = totalForStore > 0 ? excludedProductCount / totalForStore : 0;
    if (
      !setupIncomplete &&
      excludedProductCount >= EXCLUSION_ISSUE_MIN_COUNT &&
      exclusionRate > EXCLUSION_ISSUE_RATE
    ) {
      const exclusionShare = formatShare(excludedProductCount, totalForStore);
      issues.push({
        severity: 'warning',
        title: `${excludedProductCount.toLocaleString()} product${excludedProductCount === 1 ? '' : 's'} excluded by the store`,
        meta: `${excludedProductCount.toLocaleString()} of ${totalForStore.toLocaleString()} products not included in this store's sync selection (${exclusionShare}).`,
      });
    }

    if (storeViolations.length > 0) {
      issues.push({
        severity: 'warning',
        title: `${storeViolations.length} product${storeViolations.length === 1 ? '' : 's'} priced under your MAP floor`,
        meta: Array.from(new Set(storeViolations.map((v) => v.productModel))).slice(0, 3).join(', '),
      });
    }

    const worstIssueSeverity: StoreViewModel['worstIssueSeverity'] = issues.some(
      (i) => i.severity === 'blocking'
    )
      ? 'blocking'
      : issues.length > 0
        ? 'warning'
        : null;

    return {
      storeId: storeId || store.id,
      storeName,
      platforms,
      integrationConnected,
      lastSyncAt,
      includedProductCount,
      excludedProductCount,
      countsArePartial: store.countsArePartial === true,
      activeMapViolations: storeViolations.length,
      issues,
      hasIssues: issues.length > 0,
      worstIssueSeverity,
      setupIncomplete,
      setupSteps,
      authorizationApproved,
      authorizationNote: authorizationApproved ? null : authorizationCopy(authorizationStatus).detail,
      syncHealth,
    };
  });
};

/**
 * Copy for the store's dealer authorization for this brand. `unverified` is a
 * self-declared dealer claim nobody has checked yet (brands without a dealer
 * list, like ProMaster today, have no way to verify it), not a rejection — it
 * does not stop a store from syncing, so it must not read as "not approved".
 */
const authorizationCopy = (
  status: string
): { done: boolean; stateLabel: string; detail: string } => {
  switch (status) {
    case 'verified':
      return { done: true, stateLabel: 'Approved', detail: 'Dealer authorization is approved.' };
    case 'pending':
      return { done: false, stateLabel: 'In review', detail: 'Dealer authorization is waiting for review.' };
    case 'rejected':
      return { done: false, stateLabel: 'Not approved', detail: 'Dealer authorization was not approved.' };
    case 'expired':
      return { done: false, stateLabel: 'Expired', detail: 'Dealer authorization has expired.' };
    case 'unverified':
      return {
        done: false,
        stateLabel: 'Self-declared',
        detail: 'The store says it is a dealer for your brand. That has not been verified yet.',
      };
    default:
      return { done: false, stateLabel: 'Not on file', detail: 'No dealer authorization is on file for this store.' };
  }
};

const buildSetupSteps = (input: {
  brandSelected: boolean;
  authorizationStatus: string;
  integrationConnected: boolean;
  platforms: string[];
  productsSelected: boolean;
  includedProductCount: number;
  countsArePartial: boolean;
}): StoreSetupStep[] => {
  const authorization = authorizationCopy(input.authorizationStatus);
  const productCount = `${input.includedProductCount.toLocaleString()}${input.countsArePartial ? '+' : ''}`;
  return [
    {
      id: 'brand',
      label: 'Selected your brand',
      done: input.brandSelected,
      stateLabel: input.brandSelected ? 'Selected' : 'Not selected',
      detail: input.brandSelected
        ? 'This store listed your brand.'
        : 'This store has not listed your brand.',
    },
    {
      id: 'authorization',
      label: 'Authorization',
      done: authorization.done,
      stateLabel: authorization.stateLabel,
      detail: authorization.detail,
    },
    {
      id: 'integration',
      label: 'Integration',
      done: input.integrationConnected,
      stateLabel: input.integrationConnected ? input.platforms.join(', ') || 'Connected' : 'Not set up',
      detail: input.integrationConnected
        ? 'A commerce platform is connected.'
        : 'No commerce platform is connected.',
    },
    {
      id: 'products',
      label: 'Products',
      done: input.productsSelected,
      stateLabel: input.productsSelected ? `${productCount} selected` : 'None selected',
      detail: input.productsSelected
        ? 'Products from your catalog are in the sync selection.'
        : 'No products are selected to sync yet.',
    },
  ];
};

export const storeInitials = initials;
